/**
 * Date utilities for calendar-week calculations.
 */

export type StartOfWeek =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

const START_OF_WEEK_KEY = "pmm_start_of_week";

const VALID_START_DAYS: Set<string> = new Set([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);

const DAY_INDEXES: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

/**
 * Read the stored start-of-week preference from localStorage.
 * Falls back to "monday" for guests and unauthenticated users.
 */
export const getStoredStartOfWeek = (): StartOfWeek => {
  try {
    const stored = localStorage.getItem(START_OF_WEEK_KEY);
    if (stored && VALID_START_DAYS.has(stored)) {
      return stored as StartOfWeek;
    }
  } catch {
    // Ignore storage errors (e.g. private browsing).
  }
  return "monday";
};

/**
 * Persist the start-of-week preference to localStorage.
 */
export const setStoredStartOfWeek = (value: StartOfWeek): void => {
  try {
    localStorage.setItem(START_OF_WEEK_KEY, value);
  } catch {
    // Ignore storage errors.
  }
};

/**
 * Return the first day of the week containing ``date`` according to the
 * user's ``startOfWeek`` preference. Defaults to Monday.
 */
export const getWeekStart = (
  date: Date,
  startOfWeek: StartOfWeek = "monday",
): Date => {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun … 6=Sat
  const key = startOfWeek.charAt(0).toUpperCase() + startOfWeek.slice(1);
  const targetDay = DAY_INDEXES[key] ?? 1; // default Monday=1
  // Days to go back to reach the most recent target day.
  const diff = (day - targetDay + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
};

/**
 * Legacy alias for ``getWeekStart``. Kept for backwards compatibility;
 * the optional ``startOfWeek`` parameter is the US267 addition.
 */
export const getMonday = (
  date: Date,
  startOfWeek: StartOfWeek = "monday",
): Date => getWeekStart(date, startOfWeek);

export const formatDate = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

/**
 * Return the ISO date string for the start of the week containing ``date``.
 */
export const getWeekStartString = (
  date: Date,
  startOfWeek: StartOfWeek = "monday",
): string => {
  const weekStart = getWeekStart(date, startOfWeek);
  return formatDate(weekStart) + "T00:00:00Z";
};

/**
 * Return the calendar date (day of month) for a named day within a week that
 * begins on ``weekStart``. This works regardless of the user's preferred
 * week-start day because it uses the day-of-week difference between the two
 * dates.
 */
export const getDayNumber = (weekStart: string, dayName: string): string => {
  const start = new Date(weekStart);
  const startDay = start.getDay();
  const targetDay = DAY_INDEXES[dayName];
  const offset = ((targetDay ?? 0) - startDay + 7) % 7;
  const date = new Date(start);
  date.setDate(start.getDate() + offset);
  return date.getDate().toString();
};

export const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/**
 * Return the ordered weekday labels for the given week start preference.
 * For Sunday-start weeks the labels are rotated so Sunday appears first.
 */
export const getWeekdays = (startOfWeek: StartOfWeek = "monday"): string[] => {
  // DAY_INDEXES uses JavaScript day numbers (0=Sun … 6=Sat) with
  // capitalized keys.  WEEKDAYS is Monday-first (0=Mon … 6=Sun).
  // Convert: Mon=1→0, Tue=2→1, …, Sun=0→6.
  const key = startOfWeek.charAt(0).toUpperCase() + startOfWeek.slice(1);
  const jsDay = DAY_INDEXES[key] ?? 1;
  const idx = jsDay === 0 ? 6 : jsDay - 1;
  const before = WEEKDAYS.slice(0, idx);
  const after = WEEKDAYS.slice(idx);
  return [...after, ...before];
};

// ── US270: date-key helpers ───────────────────────────────────────────

/**
 * Parse a date-only string (YYYY-MM-DD) into a local-midnight Date.
 * Avoids timezone skew from ISO-string parsing.
 */
const parseDateKey = (dateKey: string): Date => {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Parse a week_start string (ISO datetime, possibly naive or with Z suffix)
 * into a local-midnight Date for the date portion only.
 */
const parseWeekStart = (weekStart: string): Date => {
  const datePart = weekStart.split("T")[0];
  return parseDateKey(datePart);
};

/**
 * Generate ISO date strings (YYYY-MM-DD) for the 7 days of the week
 * starting from ``weekStart``.
 */
export const getDateRange = (weekStart: string): string[] => {
  const start = parseWeekStart(weekStart);
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dates.push(formatDate(d));
  }
  return dates;
};

/**
 * Return the weekday label for an ISO date string relative to a week_start.
 * E.g. for week_start="2026-07-20" (Monday), dateKey="2026-07-21" → "Tuesday".
 */
export const getDayLabel = (
  weekStart: string,
  dateKey: string,
  startOfWeek: StartOfWeek = "monday",
): string => {
  const start = parseWeekStart(weekStart);
  const date = parseDateKey(dateKey);
  const diffDays = Math.round(
    (date.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );
  const weekdays = getWeekdays(startOfWeek);
  if (diffDays >= 0 && diffDays < 7) {
    return weekdays[diffDays];
  }
  // Fallback: use the date's actual weekday (un-rotated).
  const jsDay = date.getDay();
  return WEEKDAYS[jsDay === 0 ? 6 : jsDay - 1] ?? dateKey;
};

/**
 * Return the day-of-month number for an ISO date key.
 */
export const getDateDayNumber = (dateKey: string): string => {
  const d = parseDateKey(dateKey);
  return d.getDate().toString();
};
